import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { Box } from '@factorialco/deprecated-design-system'
import { BudgetFragment, EmployeesTeamsQuery, GetBudgetDocument } from 'generated/resources/graphql'
import { compact, uniq } from 'lodash'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useTeams } from 'modules/employees/hooks/useTeams'
import AddEmployeeModal from 'modules/finance/components/Budgets/Detail/AddEmployeeModal'
import EditBudgetSidepanel from 'modules/finance/components/Budgets/Detail/EditBudgetSidePanel'
import PeriodNavigation from 'modules/finance/components/Budgets/Detail/PeriodNavigation'
import PeriodStatusCard from 'modules/finance/components/Budgets/Detail/PeriodStatusCard'
import BudgetTabs from 'modules/finance/components/Budgets/Detail/Tabs'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type TeamsTeam = EmployeesTeamsQuery['teams']['teams'][number]

const BudgetDetailsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const toggleEmployeeModal = () => setSearchParams({ modal: 'employee' })

  const { budgetId } = useParams()

  const { teams, loading: loadingTeams } = useTeams()

  const [budget, setBudget] = useState<BudgetFragment>()
  const [periodStart, setPeriodStart] = useState<string>()
  const [periodEnd, setPeriodEnd] = useState<string>()

  const {
    data,
    loading: loadingBudget,
    error,
    refetch: refetchBudget,
  } = useQuery(GetBudgetDocument, {
    variables: {
      id: Number(budgetId),
      ...(periodStart && { periodStart: periodStart }),
    },
    fetchPolicy: 'cache-first',
  })
  const budgets = data?.finance?.budgetsConnection?.nodes || []
  const movements = data?.finance?.budgetMovementsConnection?.nodes || []
  const categories = data?.expenses?.categoriesConnection?.nodes || []
  const allocations = data?.finance?.budgetAllocationsConnection?.nodes || []
  const canSeeBudgets = data?.finance?._policies?.budgetsSeeBudgets?.can

  const employeeIdsWithBudget = compact(
    uniq(allocations.map((allocation) => allocation.employee?.id))
  )

  const closeModal = () => {
    setSearchParams({})
  }

  const openModal = () => {
    toggleEmployeeModal()
  }

  const handleSubmit = () => {
    refetchBudget({ id: budget?.id, periodStart: budget?.currentPeriodStart })
    setSearchParams({})
  }

  const budgetName = budgets[0]?.name
  const budgetDescription = budgets[0]?.description
  const budgetAmountCents = budgets[0]?.amountCents
  const budgetArchived = budgets[0]?.archived
  const budgetEffectiveFrom = budgets[0]?.effectiveFrom
  const budgetEffectiveUntil = budgets[0]?.effectiveUntil
  const budgetTotalAllocatedAmountCents = budgets[0]?.totalAllocatedAmountCents

  useEffect(() => {
    if (!loadingBudget) {
      setBudget(budgets[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingBudget,
    budgetName,
    budgetDescription,
    budgetAmountCents,
    budgetArchived,
    budgetEffectiveFrom,
    budgetEffectiveUntil,
    budgetTotalAllocatedAmountCents,
  ])

  const loading = !budget && (loadingBudget || loadingTeams)

  const isTrainingBudget = budget?.budgetableBy === 'training_class' && budgetId
  const revampBudgetDetailPath = navigation.expenses.budgets.detailRevamp.fullPath({ budgetId: Number(budgetId) })
  useEffect(() => {
    if (isTrainingBudget) {
      // Temporary redirect to new budget details page when the budget is training class, this is under a ff in production
      // and later on the revamp detail page will replace this page and the redirect will be removed
      navigate(revampBudgetDetailPath)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrainingBudget, revampBudgetDetailPath])

  return (
    <ResponsivePage.Body
      variant="wide"
      showHeader={false}
      authorized={canSeeBudgets}
      loading={loading}
      error={error}
    >
      {budget && (
        <Box gap="s32">
          {budget.started && (
            <PeriodNavigation
              budget={budget}
              onPeriodChange={(start, end) => {
                setPeriodStart(start)
                setPeriodEnd(end)
              }}
            />
          )}
          <PeriodStatusCard budget={budget} />
          <BudgetTabs
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            teams={teams as TeamsTeam[]}
            budget={budget}
            currency={budget.currency}
            movements={movements}
            categories={categories}
            periodStart={periodStart}
            periodEnd={periodEnd}
            openEmployeeModal={openModal}
            allocations={allocations}
            refetchBudget={refetchBudget}
          />
          {searchParams.get('modal') === 'edit' && (
            <EditBudgetSidepanel
              onClose={() => setSearchParams({})}
              openEditBudgetSidePanel={searchParams.get('modal') === 'edit'}
              budget={budget}
              onSubmit={handleSubmit}
            />
          )}
          {searchParams.get('modal') === 'employee' && (
            <AddEmployeeModal
              budget={budget}
              employeeIdsWithBudget={employeeIdsWithBudget}
              closeModal={closeModal}
              refetchBudget={refetchBudget}
            />
          )}
        </Box>
      )}
    </ResponsivePage.Body>
  )
}

export default BudgetDetailsPage
